import React, { useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CollectionModel } from '../../data-layer/collections/types';
import { COLLECTIONS_NAMESPACE_PREFIX } from '../../data-layer/posts/reducer';
import { PostModel } from '../../data-layer/posts/types';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { CollectionNotFound } from '../../ui/collection/CollectionNotFound';
import { Header } from '../../ui/collection/Header';
import { StickyFooter } from '../../ui/collection/StickyFooter';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { Button } from '../../ui/input/Button';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { InfiniteGrid } from '../../ui/layout/InfiniteGrid';
import { PostCardCompact, PostCardCompactSkeleton } from '../../ui/posts/PostCardCompact';
import { CollectionMenu } from '../../ui/users/CollectionMenu';
import { useEventSource } from '../../utils/LocationContext';
import { EventSource } from '../../utils/analyticsService';
import { ymTrackOpenCollectionLink } from '../../utils/metrika';
import { useMeta } from '../../utils/useMeta';
import { useTranslation } from '../../utils/useTranslation';
import { usePostCollection } from '../post/usePostCollection';
import { useCanonicalUrl } from '../utils/useCanonicalUrl';
import { useCollection } from './useCollection';
import { useCurrentUser } from './useCurrentUser';
import { withCollectionCallbacks } from './withCollectionCallbacks';

const PER_PAGE = 24;
const COLUMNS = 2;
// Показываем на одну строчку скелетонов меньше, чтобы провоцировать InfiniteGrid на подгрузку следующей пачки при быстром скролле
const SKELETONS_PER_PAGE = PER_PAGE - COLUMNS;
const EnhancedCollectionMenu = withCollectionCallbacks(CollectionMenu);
const EnhancedStickyFooter = withCollectionCallbacks(StickyFooter);

const buildMeta = (
    model: CollectionModel | undefined,
    firstPost: PostModel | undefined,
    canonicalUrl: string,
) => {
    if (!model || !firstPost) {
        return {};
    }

    const {
        content: [
            {
                type: contentType,
                media: [firstMedia],
            },
        ],
    } = firstPost;

    return {
        title: `Яндекс Ритм | Подборка "${model.displayName}"`,
        description: `Автор ${model.author?.displayName}`,
        links: [{ rel: 'canonical', href: canonicalUrl }],
        openGraph: {
            title: `Яндекс Ритм | Подборка "${model.displayName}`,
            description: `Автор ${model.author?.displayName}`,
            // @ts-ignore не прорастают type-guard-ы в contentType
            image: contentType === 'video' ? firstMedia.firstFrameUrl : firstMedia.url,
        },
    };
};

export const CollectionDetails = () => {
    const params = useParams();
    const navigate = useNavigate();
    const currentUser = useCurrentUser();
    const collectionId = params.collectionId!;
    const { eventSource, prevEventSource } = useEventSource();
    const { collectionModel, loadError } = useCollection({ collectionId });
    const collection = usePostCollection({
        namespace: `${COLLECTIONS_NAMESPACE_PREFIX}${collectionId}`,
        filters: { collectionId, amount: PER_PAGE },
    });
    const t = useTranslation();

    const onBack = useCallback(() => {
        if (collectionModel?.defaultCollection || collectionModel?.modifiable) {
            navigate('/profile');
        } else {
            navigate('/');
        }
    }, [navigate, collectionModel?.defaultCollection, collectionModel?.modifiable]);

    const canonicalUrl = useCanonicalUrl();
    useMeta(buildMeta(collectionModel, collection.items[0], canonicalUrl));

    const skeletonCountRef = useRef(0);
    if (!collection.isLoading) {
        skeletonCountRef.current = 0;
    }

    useEffect(() => collection.onReset, [collection.onReset]);

    useEffect(() => {
        if (collectionModel?.id && !prevEventSource && eventSource === EventSource.COLLECTION) {
            ymTrackOpenCollectionLink({
                page: 'Коллекция',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
                isPublic: collectionModel?.isPublic,
                own: collectionModel?.modifiable,
                collectionId: collectionModel?.id,
            });
        }
    }, [collectionModel?.id, eventSource, prevEventSource]);

    if (loadError) {
        return <CollectionNotFound />;
    }

    return (
        <DashboardPage>
            {collectionModel && (
                <Header
                    collectionModel={collectionModel}
                    onBack={onBack}
                    menuSlot={
                        <EnhancedCollectionMenu
                            collection={collectionModel}
                            modifiable={collectionModel.modifiable}
                        />
                    }
                />
            )}
            <Flex width="100%" minHeight="calc(100% - 72px - 64px)">
                <InfiniteGrid
                    columns={COLUMNS}
                    perPage={PER_PAGE}
                    count={collection.items.length}
                    isLoading={collection.isLoading}
                    isEndReached={collection.isEndReached}
                    onLoadNext={collection.onLoadNext}
                    renderItem={(index: number) => {
                        const postModel = collection.items[index];

                        if (postModel) {
                            return (
                                <Box>
                                    <PostCardCompact
                                        post={toPostUI(postModel)}
                                        fromCollectionId={collectionId}
                                    />
                                </Box>
                            );
                        }

                        if (collection.isLoading && skeletonCountRef.current < SKELETONS_PER_PAGE) {
                            skeletonCountRef.current++;

                            return (
                                <Box>
                                    <PostCardCompactSkeleton />
                                </Box>
                            );
                        }

                        return null;
                    }}
                />
                {collection.items.length && (
                    <Flex width="100%" spacing={1} p={2}>
                        <Button
                            width="100%"
                            as={Link}
                            to="/search?searchType=post&query="
                            color="secondary"
                        >
                            {t('Найти больше идей')}
                        </Button>
                        <Button width="100%" as={Link} to="/">
                            {t('Открыть ленту')}
                        </Button>
                    </Flex>
                )}
            </Flex>
            {collectionModel && <EnhancedStickyFooter collection={collectionModel} />}
        </DashboardPage>
    );
};
